import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'
import { doc, arrayUnion, arrayRemove } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { deleteDoc, setDoc } from '@/lib/firestore'
import { friendshipPairId } from '@/lib/friendId'
import { log } from '@/lib/logger'
import { useAuth } from '@/hooks/useAuth'
import { useFirestoreDoc } from '@/hooks/useFirestore'
import { runMutation } from '@/lib/firestoreMutation'
import { track } from '@/lib/analytics'
import { fbt } from 'fbtee'

interface BlockedUsersValue {
  blockedUserIds: string[]
  loading: boolean
  blockUser: (userId: string) => Promise<void>
  unblockUser: (userId: string) => Promise<void>
  isBlocked: (userId: string) => boolean
}

const BlockedUsersCtx = createContext<BlockedUsersValue>({
  blockedUserIds: [],
  loading: true,
  blockUser: async () => {},
  unblockUser: async () => {},
  isBlocked: () => false,
})

// eslint-disable-next-line react-refresh/only-export-components -- the context's hook lives next to its provider
export function useBlockedUsers() {
  return useContext(BlockedUsersCtx)
}

export function BlockedUsersProvider({ children }: { children: ReactNode }) {
  const { signedInUid: uid } = useAuth()

  const ref = uid ? doc(db, 'users', uid, 'private', 'blocks') : null
  const { data, loading } = useFirestoreDoc<{ blockedUserIds?: string[] }>(ref)

  const blockedUserIds = useMemo(() => data?.blockedUserIds ?? [], [data])
  const blockedSet = useMemo(() => new Set(blockedUserIds), [blockedUserIds])

  const isBlocked = useCallback((userId: string) => blockedSet.has(userId), [blockedSet])

  const blockUser = useCallback(async (userId: string) => {
    if (!uid) return
    await runMutation(fbt("Couldn't block user", 'Error toast when blocking a user fails'), async () => {
      await setDoc(doc(db, 'users', uid, 'private', 'blocks'), {
        blockedUserIds: arrayUnion(userId),
      }, { merge: true })
      track('user_blocked', {})
    })
    // Blocking someone also ends the friendship, so they leave your friends,
    // facepiles and plans. Best-effort: there may be no friendship to delete.
    try {
      await deleteDoc(doc(db, 'friendships', friendshipPairId(uid, userId)))
    } catch (err) {
      log('block', 'no friendship removed on block', { err: String(err) })
    }
  }, [uid])

  const unblockUser = useCallback(async (userId: string) => {
    if (!uid) return
    await runMutation(fbt("Couldn't unblock user", 'Error toast when unblocking a user fails'), async () => {
      await setDoc(doc(db, 'users', uid, 'private', 'blocks'), {
        blockedUserIds: arrayRemove(userId),
      }, { merge: true })
      track('user_unblocked', {})
    })
  }, [uid])

  const value = useMemo<BlockedUsersValue>(
    () => ({ blockedUserIds, loading, blockUser, unblockUser, isBlocked }),
    [blockedUserIds, loading, blockUser, unblockUser, isBlocked],
  )

  return <BlockedUsersCtx.Provider value={value}>{children}</BlockedUsersCtx.Provider>
}
